import io from 'socket.io-client';
import LZString from "lz-string";

/**
 * Supported socket operations
 */

const CONNECTED = 'connect';
const DISCONNECT = 'disconnect';
const CONNECT_ERROR = 'connect_error';

export default class WebSocketController {
  socket;

  connected = false;

  forceClosed = true;

  constructor(url, connectCallback, disconnectCallback, options) {
    this.socket = io(url, {
      reconnection: true,
      reconnectionDelay: options ? options.reconnectionDelay : 500,
      reconnectionAttempts: options ? options.reconnectionAttempts : Infinity,
      rejectUnauthorized: false,
      transports: ['websocket'],
    });
    this.initializeListeners(connectCallback, disconnectCallback);
  }

  close() {
    console.info('closing socket');
    this.forceClosed = true;
    this.socket.close();
  }

  decompressData = socketData => {
    if (typeof socketData === 'object') {
      const { data, isCompressed, dataType } = socketData;
      if (isCompressed) {
        const decompressData = LZString.decompressFromUTF16(data);
        if (dataType === 'object') {
          return JSON.parse(decompressData);
        }
        return decompressData;
      }
      return data;
    }
    return socketData || '';
  };

  customCallback = (error, data, callback) => {
    if (error) {
      callback && callback(error, data);
    } else {
      const deCompressedData = this.decompressData(data);
      callback && callback(error, deCompressedData);
    }
  };

  customSubscriber = (data, callback, storeId) => {
    const deCompressedData = this.decompressData(data);
    callback && callback(deCompressedData, storeId);
  };

  initializeListeners(connectCallback, disconnectCallback) {
    this.socket.on(CONNECTED, this.onConnected);
    this.socket.on(CONNECTED, connectCallback);
    this.socket.on(DISCONNECT, this.onDisconnected);
    if (disconnectCallback) {
      this.socket.on(DISCONNECT, reason => {
        if (this.forceClosed) {
          disconnectCallback(reason);
        }
      });
    }
    if (disconnectCallback) {
      this.socket.on(CONNECT_ERROR, disconnectCallback);
    }
    this.socket.on(CONNECT_ERROR, this.onConnectionError);
  }

  onConnected = () => {
    console.info('websocket connected');
    this.connected = true;
  };

  onDisconnected = (...args) => {
    console.info('websocket disconnected', args);
    this.connected = false;
  };

  onConnectionError = error => {
    console.warn('connection error', error);
    this.connected = false;
  };

  sendMessageUncompressed = (event, ...args) => {
    if (this.socket) {
      console.info('sending event', event, ...args);
      this.socket.emit(event, ...args);
    }
  };

  sendMessage = (event, ...args) => {
    if (this.socket && this.connected) {
      console.info('sending event', event, ...args);
      this.socket.emit(event, ...args.slice(0, args.length - 1), (...response) => this.customCallback(...response, args[args.length - 1]),
      );
    } else {
      args[args.length - 1] &&
        args[args.length - 1]({
          errorCode: 'socket_disconnected',
        });
    }
  };

  redisSubscribe = (event, storeId, callback, subscriber) => {
    console.debug('subscribing to event', event, storeId, subscriber);
    this.sendMessage(event, storeId, (error, response) => {
      callback(error, response);
      if (!error) {
        const { id } = response;
        this.socket.on(id, (...args) => this.customSubscriber(...args, subscriber, storeId));
      }
    });
  };

  subscribe = (event, storeId, callback, subscriber) => {
    console.debug('subscribing to event', event, storeId, subscriber);
    if (this.socket) {
      this.socket.emit(event, storeId, (...args) => this.customCallback(...args, callback));
      this.socket.on(`${event}`, (...args) => this.customSubscriber(...args, subscriber));
    }
  };

  subscribeEmployeeId = (event, storeId, employeeId, callback, subscriber) => {
    console.debug('subscribing to event', event, employeeId, subscriber);
    if (this.socket) {
      this.socket.emit(event, storeId, employeeId, (...args) => this.customCallback(...args, callback),
      );
      this.socket.on(event, (...args) => this.customSubscriber(...args, subscriber));
    }
  };

  subscribeExtraData = (event, storeId, extraData, callback, subscriber) => {
    console.debug('subscribing to event', event, extraData, subscriber);
    if (this.socket) {
      this.socket.emit(event, storeId, extraData, (...args) => this.customCallback(...args, callback),
      );
      this.socket.on(event, (...args) => this.customSubscriber(...args, subscriber));
    }
  };

  unsubscribe = event => {
    this.socket.off(event);
  };
}
